import { useState } from "react";
import type { Exemplar, ChecklistItem } from "../../api/content";
import type { PackageView } from "../../api/package";
import type { CheckItem } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Checks } from "../../components/Checks";
import { inputShell } from "../../components/Field";
import { FileButton } from "../../components/FileButton";
import { GateFooter } from "../../components/GateFooter";
import { Guide } from "../../components/Guide";
import { StepChecklist } from "../../components/StepChecklist";
import { OptionTile } from "../../components/OptionTile";
import { Pill } from "../../components/Pill";
import { ScreenHeader } from "../../components/ScreenHeader";
import { Section } from "../../components/Section";
const CV_MAX_BYTES = 8 * 1024 * 1024;

interface PackageScreenProps {
  view: PackageView;
  track: { label: string; host: string; tip: string };
  exemplar: Exemplar;
  checklist: ChecklistItem[];
  cvBuilders: { name: string; url: string }[];
  verifyResult: { checks: CheckItem[]; keptPrevious: boolean } | null;
  uploading: boolean;
  uploadError: string | null;
  busy: boolean;
  onVerifyPortfolio: (url: string) => void;
  onToggleSelf: (keys: string[]) => void;
  onUploadCv: (file: File) => void;
  onComplete: () => void;
}

/** A fictional model portfolio with numbered pins that map to the 10-point checklist. */
function ExemplarMock({ exemplar, checklist }: { exemplar: Exemplar; checklist: ChecklistItem[] }) {
  const pin = (key: string) => (
    <span className="mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-kicker text-[10px] font-bold text-white">
      {checklist.findIndex((c) => c.key === key) + 1}
    </span>
  );
  return (
    <div className="overflow-hidden rounded-[var(--radius-control)] border border-border">
      <div className="bg-surface-high px-3 py-1.5 text-[12px] text-text-muted">
        {pin("open")}
        {exemplar.home}
      </div>
      <div className="flex flex-col gap-3 p-4 text-[13px]">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-accent font-bold text-on-accent">
            {exemplar.init}
          </span>
          <div>
            <div className="font-bold">
              {pin("photo")}
              {exemplar.name}
            </div>
            <div className="text-text-muted">
              {pin("position")}
              {exemplar.line}
            </div>
          </div>
        </div>
        <div className="rounded-[9px] bg-accent-tint p-3">
          <div className="font-bold">
            {pin("best")}Featured: {exemplar.feature.title}
          </div>
          <div>
            {pin("casestudy")}
            <b>Problem:</b> {exemplar.feature.problem}
          </div>
          <div>
            <b>What I did:</b> {exemplar.feature.process}
          </div>
          <div>
            {pin("result")}
            <b>Result:</b> {exemplar.feature.result}
          </div>
        </div>
        <div>
          {pin("pieces")}
          {pin("context")}
          <span className="text-text-muted">More work, each shown in context</span>
          <div className="mt-1.5 grid grid-cols-3 gap-2">
            {exemplar.pieces.map((piece) => (
              <div key={piece} className="rounded-[7px] bg-surface-high p-2 text-[12px]">
                {piece}
              </div>
            ))}
          </div>
        </div>
        <div className="text-text-muted">
          {pin("contact")}
          {/* The mock's contact line uses &middot; entities; render it as plain text. */}
          {exemplar.contact.replace(/&middot;/g, "·")} {pin("recent")}Updated this month
        </div>
      </div>
    </div>
  );
}

/** Step 2 (v2): Package. A portfolio link that passes the check and a CV; the 10-point self-check is optional. */
export function PackageScreen(props: PackageScreenProps) {
  const { view } = props;
  const [url, setUrl] = useState(view.step.portfolioUrl ?? "");
  const [cvError, setCvError] = useState<string | null>(null);
  const selected = new Set(view.step.selfCheck);

  const pickCv = (file: File | undefined) => {
    setCvError(null);
    if (!file) return;
    if (!/\.(pdf|docx?)$/i.test(file.name))
      return setCvError("That needs to be a PDF or Word document.");
    if (file.size === 0) return setCvError("That file looks empty.");
    if (file.size > CV_MAX_BYTES) return setCvError("That file is too large, keep it under 8MB.");
    props.onUploadCv(file);
  };

  return (
    <div>
      <ScreenHeader
        kicker="Step 2 · Package"
        title="Build a portfolio clients trust."
        sub="A portfolio a client can open and a CV are what turn a profile into a hire. Start by seeing what good looks like."
      />
      <Guide guideKey="gig2" done={view.done} />
      <StepChecklist gate={view.gate} done={view.done} />

      <Section
        title={`1. What a hire-winning ${props.track.label} portfolio looks like`}
        hint="A fictional example. Each number marks one of the 10 things clients look for."
      >
        <ExemplarMock exemplar={props.exemplar} checklist={props.checklist} />
        <ol className="mt-3 grid grid-cols-1 gap-1 pl-5 text-[13px] sm:grid-cols-2">
          {props.checklist.map((item) => (
            <li key={item.key}>
              <b>{item.t}.</b> {item.d}
            </li>
          ))}
        </ol>
        <Banner tone="note">
          <b>Where to put it:</b> {props.track.tip}
          <br />
          <span className="text-[13px] text-text-muted">Accepted homes: {props.track.host}.</span>
        </Banner>
        <Banner tone="note">
          <b>No paid clients yet?</b> Do a self-initiated project for a real local business and
          label it "Concept". Show your process and any honest result. Never invent clients or
          numbers.
        </Banner>
      </Section>

      <Section
        title="2. Submit your portfolio link"
        hint="We check it opens, isn't raw file storage, and is the right kind of home for your skill. Only a link that passes is saved."
        status={<Pill ok={view.step.portfolioOk} okText="Verified" />}
      >
        <div className="flex flex-wrap gap-2">
          <input
            type="url"
            aria-label="Your portfolio link"
            placeholder="https://..."
            className={`${inputShell} min-w-[220px] flex-1`}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
          <Button
            size="sm"
            disabled={!url.trim()}
            onClick={() => props.onVerifyPortfolio(url.trim())}
          >
            Verify
          </Button>
        </div>
        <Checks checks={props.verifyResult?.checks ?? view.portfolioChecks} />
        {props.verifyResult?.keptPrevious ? (
          <p className="mt-1.5 text-[12.5px] text-text-muted">
            Your accepted link is unchanged: {view.step.portfolioUrl}
          </p>
        ) : null}
        <details className="mt-4 rounded-[var(--radius-control)] border border-border bg-surface p-3">
          <summary className="cursor-pointer text-[13.5px] font-bold">
            Check yours against the 10 points (optional) · {view.selfCount} of 10
          </summary>
          <p className="mt-2 text-[13px] text-text-muted">
            What clients look for first. Open your portfolio and tick only what's genuinely there.
          </p>
          <div className="mt-2 flex flex-col gap-2">
            {props.checklist.map((item) => (
              <OptionTile
                key={item.key}
                selected={selected.has(item.key)}
                title={item.t}
                detail={item.d}
                onToggle={() => {
                  const next = new Set(selected);
                  if (next.has(item.key)) next.delete(item.key);
                  else next.add(item.key);
                  props.onToggleSelf(Array.from(next));
                }}
              />
            ))}
          </div>
        </details>
      </Section>

      <Section
        title="3. Upload your CV"
        hint="Required. A one-page CV for platforms and clients that ask for one."
        status={<Pill ok={view.step.hasCv} okText="On file" />}
      >
        <FileButton
          label="Upload my CV"
          ariaLabel="Upload your CV"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          busy={props.uploading}
          hasFile={view.step.hasCv}
          onFile={pickCv}
        />
        <p className="mt-2 text-[13px] text-text-muted">
          {props.uploading
            ? "Uploading..."
            : view.step.hasCv
              ? `✓ ${view.step.cvName ?? "Your CV"} on file.`
              : "PDF or Word document, under 8MB."}
        </p>
        {cvError || props.uploadError ? (
          <p role="alert" className="text-[13px] text-negative">
            {cvError ?? props.uploadError}
          </p>
        ) : null}
        <p className="mt-2 text-[13px] text-text-muted">No CV yet? Build one free:</p>
        <div className="flex flex-wrap gap-2">
          {props.cvBuilders.map((builder) => (
            <a
              key={builder.name}
              href={builder.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-8 items-center rounded-[var(--radius-chip)] border border-border px-3 text-[12.5px] font-semibold text-accent"
            >
              {builder.name} ↗
            </a>
          ))}
        </div>
      </Section>

      {view.gate.pass && !view.done ? (
        <Banner tone="good">✓ Portfolio verified and CV on file. You can complete Step 2.</Banner>
      ) : null}
      <GateFooter
        gate={view.gate}
        done={view.done}
        label="Complete Step 2"
        next={{ to: "/gig/3", label: "Continue to Step 3" }}
        busy={props.busy}
        onComplete={props.onComplete}
      />
    </div>
  );
}
